import type { FC } from 'react';
import { Button, Column, Grid, InlineLoading, Layer, Stack, Tile } from '@carbon/react';
import { ArrowRight } from '@carbon/icons-react';
import { Receipt, Support } from '@carbon/pictograms-react';

import type { DadosDoAjuste } from '../../../logica/dinheiro/useAjustarPlano';
import { CarregandoPagina } from '../../padroes/Carregando';
import { EscolherPerfisAtivos } from '../../padroes/EscolherPerfisAtivos';
import { PaginaDeConta } from '../../padroes/PaginaDeConta';
import { ROTULOS } from '../../padroes/rotulos';
import { Vazio } from '../../padroes/Vazio';
import { Adicionais, CampoDoCupom, ErroDaCobranca, Recebedor, Resumo } from './Pecas';
import styles from './Dinheiro.module.scss';

// AJUSTAR O PLANO (/planos/ajustar), no Carbon: à esquerda, quantos perfis e quanto espaço a
// assinatura cobre, e o cupom; à direita, a diferença (hoje você paga, passa a pagar, a partir de
// quando) e o confirmar. Quem não pode ajustar (em atraso, sem assinatura, Pix Automático antigo)
// vê o porquê e o caminho.

const TRILHA = [{ rotulo: 'Seus perfis', caminho: '/artists' }, { rotulo: 'Planos', caminho: '/planos' }];

export const AjustarPlano: FC<{ a: DadosDoAjuste }> = ({ a }) => {
  if (a.estado === 'carregando') return <CarregandoPagina />;
  if (a.estado !== 'pronto') {
    const caso = {
      emAtraso: { pictograma: Receipt, titulo: 'Regularize o pagamento para ajustar o plano', texto: 'Sua assinatura tem uma cobrança em aberto. Assim que ela for paga, você inclui perfis e espaço por aqui.', botao: 'Ver a cobrança em aberto', fazer: a.irParaPagamentos },
      cancelada: { pictograma: Receipt, titulo: 'Sua assinatura foi cancelada', texto: 'Não há mais cobranças, e o PRO segue até o fim do período já pago. Para mudar perfis ou espaço, assine de novo depois dessa data.', botao: 'Ver o seu plano', fazer: a.voltar },
      semAssinatura: { pictograma: Receipt, titulo: 'Só uma assinatura ativa pode ser ajustada', texto: 'Perfis e armazenamento são adicionais da assinatura. Assine o Maestra PRO para escolher as quantidades.', botao: 'Ver os planos', fazer: a.voltar },
      pelosuporte: { pictograma: Support, titulo: 'Seu plano muda pelo suporte', texto: 'Sua assinatura é paga por Pix Automático, e a autorização que você deu ao banco vale para o valor atual. Para incluir perfis ou espaço, fale com a gente: ajustamos e você autoriza o valor novo.', botao: 'Falar com o suporte', fazer: a.irParaSuporte },
    }[a.estado];
    return (
      <PaginaDeConta titulo='Upgrade do plano' trilha={TRILHA}>
        <Vazio pictograma={caso.pictograma} titulo={caso.titulo} texto={caso.texto}
          acao={<Button kind='primary' size='md' renderIcon={ArrowRight} onClick={caso.fazer}>{caso.botao}</Button>} />
      </PaginaDeConta>
    );
  }

  return (
    <PaginaDeConta titulo='Upgrade do plano' descricao='Escolha quantos perfis e quanto espaço sua assinatura cobre. O resumo acompanha a escolha.'
      trilha={TRILHA} larga>
      <Grid narrow className={styles.checkout}>
        <Column sm={4} md={8} lg={9}>
          <Stack gap={6}>
            <Tile className={styles.bloco}>
              <Layer>
                <Stack gap={6}>
                  <h2 className={styles.tituloDoBloco}>Perfis e armazenamento</h2>
                  <Adicionais linhas={a.adicionais} />
                </Stack>
              </Layer>
            </Tile>
            <Tile className={styles.bloco}>
              <Layer>
                <Stack gap={6}>
                  <h2 className={styles.tituloDoBloco}>Desconto</h2>
                  <CampoDoCupom c={a.cupom} />
                </Stack>
              </Layer>
            </Tile>
          </Stack>
        </Column>
        <Column sm={4} md={8} lg={7}>
          <Resumo titulo='Resumo da alteração' item={a.item} linhas={a.linhas}>
            <p className={styles.apoio}>{a.explicacao}</p>
            <ErroDaCobranca texto={a.impedimento} tipo='warning' titulo='Antes de reduzir' />
            {a.enviando
              ? <InlineLoading description='Ajustando o plano…' iconDescription={ROTULOS.carregando} />
              : (
                <Button kind='primary' size='lg' renderIcon={ArrowRight} disabled={!a.mudou || !!a.impedimento} onClick={a.confirmar} className={styles.botaoCheio}>
                  {a.mudou ? 'Confirmar alteração' : 'Nada mudou no plano'}
                </Button>
              )}
            <Recebedor />
          </Resumo>
        </Column>
      </Grid>
      {/* Mais perfis com perfis travados na conta: qual liberar (C5). */}
      <EscolherPerfisAtivos e={a.escolha} />
    </PaginaDeConta>
  );
};
